import { Button } from '@/components/ui/button';
import {
  calculateTotalDiscountPerPriority,
  formatIDR,
  generatePromotionsPayload,
  parseErrorMessage,
} from '@/lib/utils';
import { ThemeType } from '@/stores/builder';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { HttpTypes } from '@/types';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { usePromotionStore } from '@/stores/promotion';
import { shallow } from 'zustand/shallow';
import { useMutateApplyPromotion } from '@/hooks/api/storefront/promotion';
import { AxiosError } from 'axios';
import { toast } from '@/components/ui/use-toast';
import { updateCartCheckout } from '@/lib/actions/checkout/action';
import { Tag } from 'lucide-react';

type FixedCheckoutButtonProps = {
  theme: ThemeType;
  cart: HttpTypes.StoreCart;
  preview?: boolean;
};

export default function FixedCheckoutButton({ theme, cart, preview }: FixedCheckoutButtonProps) {
  const router = useRouter();
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const token = Cookies.get('accessToken') || '';
  const cartItems = cart?.items || [];

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subtotal, setSubtotal] = useState<number>(0);

  const { selectedPromotion, selectedFreeGift } = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      selectedFreeGift: state.selectedFreeGift,
    }),
    shallow
  );

  const { mutateAsync: mutateApplyPromotion } = useMutateApplyPromotion({
    onError: async (error) => {
      if (error instanceof AxiosError) {
        if (error.status === 401) {
          await fetch('/api/remove-cart-cookie', { method: 'GET' });

          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Something went wrong',
            variant: 'destructive',
          });

          return (window.location.href = '/');
        }
      }
    },
  });

  const handleToCheckout = async () => {
    try {
      setIsLoading(true);

      const promotions_payload = {
        cart_id: cart?.cartId,
        promotions: generatePromotionsPayload(selectedPromotion, selectedFreeGift),
      };

      if (promotions_payload?.promotions?.length > 0) {
        await mutateApplyPromotion({
          body: {
            cart_id: cart?.cartId as string,
            promotions: promotions_payload.promotions,
          },
          token,
        });
      }

      const result = await updateCartCheckout(host as string);

      if (result.success) {
        return router.push(`/checkout/${result.data.checkout_id}`);
      }

      if (!cart.items?.length) {
        return toast({
          title: 'Error',
          description: 'Cart is empty',
          variant: 'destructive',
        });
      }

      if (result.status === 404) {
        await fetch('/api/remove-cart-cookie', { method: 'GET' });

        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });

        return (window.location.href = '/');
      }

      if (result.status === 400) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data.data || error.response?.data?.message;
        toast({
          title: 'Error',
          description: parseErrorMessage(errorMessage) || 'Something went wrong',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const discount = useMemo(() => {
    return calculateTotalDiscountPerPriority(selectedPromotion, subtotal);
  }, [selectedPromotion, subtotal]);

  useEffect(() => {
    const newSubtotal = cartItems.reduce((total, item) => {
      return total + (item.discount_price ? item.discount_price : item.sell_price) * item.quantity;
    }, 0);
    setSubtotal(newSubtotal || 0);
  }, [cartItems]);

  const disableButton = cart?.item_stock?.some((item) => item.stock === 0);

  return (
    <div
      className='shadow-grey-200 fixed bottom-0 left-1/2 z-[50] w-full -translate-x-1/2 justify-between rounded-t-lg border border-gray-200 bg-white p-4 shadow-[0_-2px_10px_rgba(0,0,0,0.15)] md:w-[65%] md:p-3 lg:max-h-[160px]'
      id='fixed-checkout-cart'
      style={
        {
          '--text-secondary': theme?.cart?.pageCartColorScheme.textSecondary || 'inherit',
        } as React.CSSProperties
      }
    >
      <div className='w-full space-y-2'>
        <div className='flex items-center justify-between'>
          <span className='text-base'>Estimated total</span>
          <h1 className='text-2xl font-bold text-right'>
            {formatIDR(Math.max(subtotal - discount.totalDiscount, 0))}
          </h1>
        </div>
        <div className='flex justify-end'>
          {((!preview && discount.totalDiscount > 0) || preview) && (
            <div className='flex items-center gap-2'>
              <Tag className='w-3 h-3' />
              <span className='text-sm text-red-500'>
                Saved {formatIDR(discount.totalDiscount)}
              </span>
            </div>
          )}
        </div>
        <div className='flex items-center justify-end gap-4 pt-4 md:flex-row md:items-center'>
          <Button
            className='theme_button theme_cart_page_btn flex h-12 w-full items-center md:w-[200px]'
            disabled={isLoading || disableButton || preview}
            onClick={handleToCheckout}
          >
            {isLoading && <LoadingAnimationIcon />}
            <span className='mx-2'>{theme?.cart?.labelCheckout || 'Checkout'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
